import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { ArrowLeft, Trash2, RotateCcw, Crown, AlertTriangle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Trash() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retention, setRetention] = useState(30);
  const [gated, setGated] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/trash`, { withCredentials: true });
      setItems(r.data.items || []);
      setRetention(r.data.retention_days || 30);
      setGated(false);
    } catch (e) {
      if (e.response?.status === 402) {
        setGated(true);
      } else {
        toast.error(e.response?.data?.detail || 'Failed to load trash');
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    try {
      await axios.post(`${API}/trash/${id}/restore`, {}, { withCredentials: true });
      toast.success(t('restore') || 'Restored');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="user-trash-page">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-zinc-400" data-testid="trash-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('back') || 'Back'}
          </Button>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <Trash2 className="w-4 h-4 text-[#06B6D4]" />
            {t('trashTitle') || 'My Trash'}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-14 w-full flex-1">
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-3">{t('trashTitle') || 'My Trash'}</h1>
        <p className="text-zinc-500 mb-8">
          Deleted projects stay here for {retention} days before permanent removal. Restore any project to get it back into your dashboard.
        </p>

        {gated ? (
          <div className="rounded-2xl border border-[#06B6D4]/30 bg-gradient-to-br from-[#06B6D4]/5 to-transparent p-10 text-center">
            <Crown className="w-14 h-14 text-[#06B6D4] mx-auto mb-5" />
            <h2 className="text-2xl font-black text-white mb-2">Trash recovery is a Pro feature</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">{t('trashProOnly') || 'Upgrade to Pro to restore your own deleted projects. Admin can still restore items on request.'}</p>
            <Button onClick={() => navigate('/pricing')} className="bg-white text-black hover:bg-zinc-200 rounded-full px-6" data-testid="trash-upgrade-cta">
              <Crown className="w-4 h-4 mr-2" /> {t('upgrade') || 'Upgrade'}
            </Button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-40 text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-[#1E1E24] rounded-2xl">
            <Trash2 className="w-14 h-14 text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-500">{t('trashEmpty') || 'Nothing in your trash.'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map(it => (
              <div key={it.project_id} className="bg-[#0A0A0C] border border-[#1E1E24] rounded-lg p-4 flex items-start justify-between gap-3" data-testid={`user-trash-item-${it.project_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono uppercase text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">deleted</span>
                    <span className="text-[10px] font-mono text-zinc-500">{it.genre || 'game'}</span>
                    {it.days_left != null && (
                      <span className="text-[10px] font-mono text-zinc-500 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3 text-amber-500" /> expires in {it.days_left}d
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-white font-semibold truncate">{it.name || '(untitled)'}</p>
                  {it.description && <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{it.description}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => restore(it.project_id)} className="text-emerald-400 hover:bg-emerald-500/10" data-testid={`user-trash-restore-${it.project_id}`}>
                    <RotateCcw className="w-4 h-4 mr-1" /> {t('restore') || 'Restore'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
